import { Button, Checkbox, Divider } from "@mui/material";
import React, { useState, useEffect, Fragment, useMemo } from "react";
import { STATES } from "../constants";
import CircularProgress from "@mui/material/CircularProgress";
import { useNotification } from '../context/NotificationContext';
import { ccNewSchema, ccEditSchema } from "../validations/ccEditValidation";
import BasicModalConfirmation from "./basicModalConfirmation";
import { parseToEasternTime } from './../utils/dates';
import { useCreateCreditCardMutation, useEditCreditCardMutation, useLazyGetCreditPrivateDataQuery, useGetCardTypesQuery, useGetBrandNamesQuery } from "../services/creditCards";
import { useGetAccountsFastListQuery } from "../services/accountsApi";

function PrivateDataEmptyInputs({ setNewCreditCard, newCreditCard }) {
  return (
    <Fragment>
      <div style={{ display: "flex" }}>
        <div className="input-column">
          <label>Card Number*</label>
          <input
            label="Card Number"
            name="cardNumber"
            value={newCreditCard.cardNumber}
            onChange={(e) =>
              setNewCreditCard({ ...newCreditCard, cardNumber: e.target.value, last4Digits: e.target.value.slice(-4) })
            }
          />
        </div>

        <div className="input-column">
          <label>CVV Code*</label>
          <input
            className="small-input"
            label="CVV Code"
            name="ccvCode"
            value={newCreditCard.cvvCode}
            onChange={(e) =>
              setNewCreditCard({ ...newCreditCard, cvvCode: e.target.value })
            }
          />
        </div>

          <div className="input-column">
          <label>Add'l Code</label>
          <input
            className="small-input"
            label="Add'l Code"
            name="additionalCode"
            value={newCreditCard.additionalCode  || ""}
            onChange={(e) =>
              setNewCreditCard({ ...newCreditCard, additionalCode: e.target.value })
            }
          />
        </div>

      </div>
    </Fragment>
  );
}

function PrivateData({ setNewCreditCard, newCreditCard }) {
  const [trigger, { data, isLoading, error }] = useLazyGetCreditPrivateDataQuery();

  async function requestPrivateData() {
    if (newCreditCard.id) {
      trigger(newCreditCard.id);
    }
  }

  useEffect(() => {
    if (data) {
      setNewCreditCard({
        ...newCreditCard,
        cardNumber: data.response.number,
        cvvCode: data.response.cvv,
        additionalCode: data.response.additionalCode,
      });
    }
  }, [data]);

  return (
    <Fragment>
      {!newCreditCard.cardNumber ? (
        <div>
          <Button variant="outlined" onClick={requestPrivateData} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'SHOW CC NUMBER AND CVV'}
          </Button>
          {error && <p>Error fetching private data</p>}
        </div>
      ) : (
        <Fragment>
          <div style={{ display: "flex" }}>
            <div className="input-column">
              <label>Card Number*</label>
              <input
                label="Card Number"
                name="cardNumber"
                value={newCreditCard.cardNumber}
                onChange={(e) =>
                  setNewCreditCard({
                    ...newCreditCard,
                    cardNumber: e.target.value,
                    last4Digits: e.target.value.slice(-4),
                  })
                }
              />
            </div>

            <div className="input-column">
              <label>CVV Code*</label>
              <input
                className="small-input"
                label="CVV Code"
                name="ccvCode"
                value={newCreditCard.cvvCode}
                onChange={(e) =>
                  setNewCreditCard({ ...newCreditCard, cvvCode: e.target.value })
                }
              />
            </div>
              
            <div className="input-column">
              <label>Add'l Code</label>
              <input
                className="small-input"
                label="Add'l Code"
                name="additionalCode"
                value={newCreditCard.additionalCode  || ""}
                onChange={(e) =>
                  setNewCreditCard({ ...newCreditCard, additionalCode: e.target.value })
                }
              />
            </div>

          </div>
        </Fragment>
      )}
    </Fragment>
  );
}

export default function EditCreditCardsModal(props) {

  const { showNotification } = useNotification();

  let { creditCard, setEditing, onSubmit } = props;

  let editingCard = typeof creditCard === "object";

  let initialState = editingCard ? creditCard : { isActive: true }; //creditCard can be true if we are creating a new credit card
  let [newCreditCard, setNewCreditCard] = useState({ ...initialState });
  const [validationErrors, setValidationErrors] = useState([]);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

  const [editCreditCard] = useEditCreditCardMutation();
  const [createCreditCard] = useCreateCreditCardMutation();

  const [isLoading, setIsLoading] = useState(false);

  const { data: cardTypesApiRes, isLoading: cardTypesLoading } = useGetCardTypesQuery();
  const { data: cardBrandsApiRes, isLoading: cardBrandsLoading } = useGetBrandNamesQuery();
  const { data: users = [], isLoading: usersIsLoading } = useGetAccountsFastListQuery();

  async function onLocalSubmit() {
    setValidationErrors([]);

    try {
      let newCreditCardTrimmed = { ...newCreditCard };
      console.log(newCreditCardTrimmed);

      const mutationFn = editingCard ? editCreditCard : createCreditCard;
      const validationSchema = editingCard ? ccEditSchema : ccNewSchema;

      // Validate the credit card data using Yup
      await validationSchema.validate(newCreditCardTrimmed, { abortEarly: false });

      setIsLoading(true);

      // Execute the mutation (edit or create)
      mutationFn(newCreditCardTrimmed)
        .unwrap()
        .then((res) => {
          if (res.success) {
            showNotification('Saved!', 'success');
            setEditing(false);
          } else {
            showNotification('Error creating/editing Credit Card', 'error');
          }
        })
        .catch(() => showNotification('Error creating/editing Credit Card', 'error'))
        .finally(() => setIsLoading(false));

    } catch (err) {
      console.log(err.errors);
      console.log(err.name);

      if (err.name === 'ValidationError') {
        setValidationErrors(err.errors);
      }
    }
  }

  function deleteCreditCard() {
    let deleteBodyRequest = {
      id: newCreditCard.id,
      isDeleted: true
    };
    onSubmit(deleteBodyRequest, editingCard);
  }

  const cardTypes = useMemo(() => cardTypesApiRes?.data || [], [cardTypesApiRes]);
  const cardBrands = useMemo(() => cardBrandsApiRes?.data || [], [cardBrandsApiRes]);

  if (cardTypesLoading || cardBrandsLoading || usersIsLoading) {
    return <CircularProgress size={24} />;
  } 

  return (
    <>

      <div className="admin-modal-container">
        <div className="title-id">
          <h4 style={{ fontWeight: "600", color: "var(--orange)" }}>
            Credit Cards Details
          </h4>
          {editingCard && (<p>ID: {creditCard.id}</p>)}
        </div>

        <div className="row-modal-column">
          <div className="column-modal-row">
            <div className="input-column">
              {usersIsLoading || !users ? <CircularProgress size={24} /> :
                <>
                  <label>Assigned User*</label>
                  <select
                    label="Card Brand"
                    name="card_brand"
                    value={newCreditCard.assignedUserID || ""}
                    onChange={(e) =>
                      setNewCreditCard({ ...newCreditCard, assignedUserID: e.target.value })
                    }
                  >
                    {!newCreditCard.assignedUserID && (
                      <option disabled key={"default"} value="" hidden>-- select an option --</option>
                    )}
                    {([...users])
                      .sort((a,b) => a.fullName.localeCompare(b.fullName))
                      .map((user) => (
                        <option value={user.id} key={user.id}>
                          {user.fullName} {user.isDeleted ? '(deleted)' : ''}
                        </option>
                    ))}
                  </select>
                </>
              }
            </div>

            <div className="checkbox-200px">
              <Checkbox
                id="activeCard"
                name="Active Card"
                checked={newCreditCard.isActive}
                onChange={(e) =>
                  setNewCreditCard({
                    ...newCreditCard,
                    isActive: !newCreditCard.isActive,
                  })
                }
              />
              <label htmlFor="activeCard" style={{ fontSize: "16px" }}>
                Active Card
              </label>
            </div>
          </div>

          <Divider style={{ width: "100%", margin: "0 0 1em 0" }} />

          <div className="column-modal" style={{ margin: "0" }}>
            <div style={{ display: "flex" }}>
              <div className="input-column">
                <label>Card Type*</label>
                <select
                  label="Card Type"
                  name="card_type"
                  value={newCreditCard.cardTypeID || ""}
                  onChange={(e) =>
                    setNewCreditCard({ ...newCreditCard, cardTypeID: e.target.value })
                  }
                >
                  {!newCreditCard.cardTypeID && (
                    <option disabled key={"default"} value="" hidden>-- select an option --</option>
                  )}
                  {([...cardTypes])
                    .sort((a,b) => a.name.localeCompare(b.name))
                    .map((cardType) => (
                    <option value={cardType.id} key={cardType.id}>{cardType.name}</option>
                  ))}
                </select>
              </div>

              <div className="input-column">
                <label>Card Brand*</label>
                <select
                  label="Card Brand"
                  name="card_brand"
                  value={newCreditCard.cardBrandID || ""}
                  onChange={(e) =>
                    setNewCreditCard({ ...newCreditCard, cardBrandID: parseInt(e.target.value) })
                  }
                >
                  {!newCreditCard.cardBrandID && (
                    <option disabled key={"default"} value="" hidden>-- select an option --</option>
                  )}
                  { ([...cardBrands])
                    .sort((a,b) => a.name.localeCompare(b.name))
                    .map((cardBrand) => (
                    <option value={cardBrand.id} key={cardBrand.id}>{cardBrand.name}</option>
                  ))}
                </select>
              </div>

              <div className="input-column">
                <label>Name On Card*</label>
                <input
                  label="Name On Card"
                  name="nameOnCard"
                  value={newCreditCard.nameOnCard}
                  onChange={(e) =>
                    setNewCreditCard({ ...newCreditCard, nameOnCard: e.target.value })
                  }
                />
              </div>
            </div>

            <div style={{ display: "flex" }}>
              <div className="input-column">
                <label>City</label>
                <input
                  label="City"
                  name="city"
                  value={newCreditCard.city}
                  onChange={(e) =>
                    setNewCreditCard({ ...newCreditCard, city: e.target.value })
                  }
                />
              </div>

              <div className="input-column">
                <label>State</label>
                <select
                  label="State"
                  name="state"
                  value={newCreditCard.state || ""}
                  onChange={(e) =>
                    setNewCreditCard({ ...newCreditCard, state: e.target.value })
                  }
                >
                  {!newCreditCard.state && (
                    <option disabled key={"default"} value="" hidden>-- select an option --</option>
                  )}
                  {STATES.map((oneState) => (
                    <option value={oneState.abbreviation} key={oneState.abbreviation}>{oneState.name}</option>
                  ))}
                </select>
              </div>

              <div className="input-column">
                <label>Address</label>
                <input
                  label="Address"
                  name="address"
                  value={newCreditCard.address}
                  onChange={(e) =>
                    setNewCreditCard({ ...newCreditCard, address: e.target.value })
                  }
                />
              </div>

              <div className="input-column">
                <label>ZIP</label>
                <input
                  label="ZIP"
                  name="zip"
                  value={newCreditCard.zip}
                  onChange={(e) =>
                    setNewCreditCard({ ...newCreditCard, zip: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <Divider style={{ width: "100%", margin: "0 0 1em 0" }} />

          <div className="column-modal-row">
            <div>
              <Fragment>
                {editingCard ? (
                  <PrivateData
                    setNewCreditCard={setNewCreditCard}
                    newCreditCard={newCreditCard}
                  />
                ) : (
                  <PrivateDataEmptyInputs
                    setNewCreditCard={setNewCreditCard}
                    newCreditCard={newCreditCard}
                  />
                )}
              </Fragment>
            </div>

            <div style={{ display: "flex" }}>
              <div className="input-column">
                <label>EXP Month*</label>
                <input
                  className="small-input"
                  label="EXP Month"
                  name="expMonth"
                  value={newCreditCard.expirMonth}
                  onChange={(e) =>
                    setNewCreditCard({
                      ...newCreditCard,
                      expirMonth: e.target.value,
                    })
                  }
                />
              </div>

              <div className="input-column">
                <label>EXP Year*</label>
                <input
                  className="small-input"
                  label="EXP Year"
                  name="expYear"
                  value={newCreditCard.expirYear}
                  onChange={(e) =>
                    setNewCreditCard({
                      ...newCreditCard,
                      expirYear: e.target.value,
                    })
                  }
                />
              </div>

              <div className="input-column">
                <label>Last 4 Digits</label>
                <input
                  className="small-input"
                  label="Last 4 Digits"
                  name="last4Digits"
                  style={{border: "none"}}
                  disabled={true}
                  value={newCreditCard.last4Digits}
                />
              </div>
            </div>
          </div>
          </div>
          {/* -----------------------ERROR PROMPT-------------------------------- */}

        <ul style={{ color: 'red', margin:'0 0 0.5em 0' }} >
        {validationErrors.map(errMsg => <li>{errMsg}</li>)}
        </ul>

        {/* ------------------------------------------------------------------- */}

        <div className="modal-buttons-withDelete">
          {editingCard && (
            <Button
              variant="outlined"
              style={{
                color: "var(--red)",
                background: "white",
                borderColor: "var(--red)",
                width: "200px",
                whiteSpace: "nowrap",
              }}
              onClick={() => setShowDeleteConfirmationModal(true)}
            >
              Delete Credit Card
            </Button>
          )}

          <div className="modal-buttons">
            <Button
              variant="contained"
              color="secondary"
              style={{ width: "100px" }}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              style={{ width: "100px", margin: " 0 0 0 1em" }}
              onClick={onLocalSubmit}
              disabled={isLoading}
              name="SaveCreditCardEdit"
              className="SaveCreditCardEdit"
            >
              {isLoading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Save"}
            </Button>
          </div>
        </div>

        {editingCard && (
          <div className="modal-footer">
            <p style={{ color: "var(--grey-inactive)", margin: "0 0.5em 0 0" }}>
              Created by{" "}
            </p>
            <p style={{ margin: "0 1em 0 0" }}>
              {users.find((user) => user.id === creditCard.createdBy)?.fullName}{" "}
              -{" "}
              {creditCard.createdOn
                ? parseToEasternTime(creditCard.createdOn)
                : null}
            </p>
            <p style={{ color: "var(--grey-inactive)", margin: "0 0.5em 0 0" }}>
              Modified by{" "}
            </p>{" "}
            <p>
              {users.find((user) => user.id === creditCard.modifiedBy)?.fullName}{" "}
              -{" "}
              {creditCard.modifiedOn
                ? parseToEasternTime(creditCard.modifiedOn)
                : null}
            </p>
          </div>
        )}

        <BasicModalConfirmation
          title={`Are you sure you want to delete this credit card?`}
          confirmLabel={"Yes"}
          cancelLabel={"No"}
          open={showDeleteConfirmationModal}
          onClick={() => {
            deleteCreditCard();
          }}
          handleClose={() => setShowDeleteConfirmationModal(false)}
        />
      </div>
    </>
  );
}
